import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import MainNav from "@/components/MainNav";
import { getTribeArtists, getHiddenArtists } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Individual artist card with cached Tribe image
function ArtistCard({ artist, index, onOpen, priorityLoad }) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Use the Tribe image endpoint (now with database caching)
  const imageUrl = `${BACKEND_URL}/api/tribe/artists/${artist.id}/image`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: Math.min(index * 0.03, 0.3) }}
      onClick={() => onOpen(artist.slug)}
      className="cursor-pointer group"
      data-testid={`artist-card-${artist.slug}`}
    >
      <div className="aspect-square rounded-lg overflow-hidden mb-3 bg-zinc-900 border border-zinc-800 group-hover:border-zinc-500 transition-all relative">
        {artist.has_image && !imageError ? (
          <>
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-zinc-900">
                <span className="text-4xl font-syne text-zinc-700 animate-pulse">
                  {artist.name.charAt(0)}
                </span>
              </div>
            )}
            <img
              src={imageUrl}
              alt={artist.name}
              data-grayscale="true"
              className={`w-full h-full object-cover transition-all duration-300 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              loading={priorityLoad ? "eager" : "lazy"}
              decoding="async"
              fetchpriority={priorityLoad ? "high" : "low"}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
            />
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-800 to-zinc-900">
            <span className="text-5xl font-syne text-zinc-600 group-hover:text-zinc-400 transition-colors">
              {artist.name.charAt(0)}
            </span>
          </div>
        )}
      </div>
      <div className="text-center">
        <div className="text-sm font-medium text-zinc-400 group-hover:text-white transition-colors">
          {artist.name}
        </div>
      </div>
    </motion.div>
  );
}

export default function ManagementPage() {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArtists = async () => {
      try {
        // Fetch artists and hidden list in parallel
        const [artistsRes, hiddenRes] = await Promise.all([
          getTribeArtists(),
          getHiddenArtists().catch(() => ({ data: [] }))
        ]);
        
        const hiddenIds = hiddenRes.data || [];
        
        // Filter out hidden artists and sort alphabetically
        const visibleArtists = (artistsRes.data || [])
          .filter(artist => !hiddenIds.includes(artist.id))
          .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
        
        setArtists(visibleArtists);
      } catch (err) {
        console.error("Error fetching artists:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchArtists();
  }, []);

  const openArtistPage = (slug) => {
    window.open(`https://tribe-mail-feature.emergent.host/${slug}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-background" data-testid="management-page">
      <MainNav />

      <main className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-3xl md:text-4xl font-syne font-bold text-white" data-testid="management-title">Management</h1>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                <div key={i}>
                  <Skeleton className="aspect-square rounded-lg mb-3" />
                  <Skeleton className="h-5 w-24 mx-auto" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8" data-testid="artists-grid">
              {artists.map((artist, index) => (
                <ArtistCard 
                  key={artist.id} 
                  artist={artist} 
                  index={index} 
                  onOpen={openArtistPage}
                  priorityLoad={index < 6}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      <footer className="border-t border-zinc-800 py-6">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-zinc-600 text-xs">© 2025 Underplay. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
